/**
 * Live Session Details corner panel — matches Figma StudentCornerPanels.
 */

import React from "react";
import { ChevronDown, Clock, Info, Users } from "lucide-react";

export type SessionDetailsData = {
  sessionName: string;
  topicCovered: string;
  courseLabel?: string;
  batchLabel?: string;
  category?: string;
  trainerName?: string;
  trainerAvatarUrl?: string;
  streamingMode?: string;
  sessionTimer?: string;
};

type SessionDetailsPopoverProps = {
  open: boolean;
  details: SessionDetailsData;
  onClose: () => void;
};

function DetailRow({
  label,
  value,
  mono,
  highlight,
  capitalize,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
  capitalize?: boolean;
}) {
  if (!value) return null;
  return (
    <div>
      <p
        style={{
          margin: 0,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#64748b",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "2px 0 0",
          fontSize: mono ? 11 : 13,
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, monospace"
            : "inherit",
          fontWeight: highlight ? 600 : 500,
          color: highlight ? "#6ee7b7" : "#e2e8f0",
          textTransform: capitalize ? "capitalize" : undefined,
          lineHeight: 1.35,
          wordBreak: "break-word",
          whiteSpace: "normal",
        }}
      >
        {value}
      </p>
    </div>
  );
}

export function SessionDetailsPopover({
  open,
  details,
  onClose,
}: SessionDetailsPopoverProps) {
  if (!open) return null;

  const trainerName = details.trainerName?.trim() || "Trainer";
  const initial = trainerName.charAt(0).toUpperCase() || "T";
  const streamingLabel = (details.streamingMode || "one-way")
    .replace(/_/g, " ")
    .replace(/-/g, " ");

  return (
    <div
      style={{
        position: "absolute",
        left: 16,
        bottom: "100%",
        marginBottom: 8,
        zIndex: 40,
        width: "min(360px, calc(100vw - 2rem))",
        pointerEvents: "auto",
      }}
      role="dialog"
      aria-label="Live Session Details"
    >
      <div
        style={{
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.1)",
          background: "rgba(26,29,38,0.97)",
          padding: 16,
          boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
          backdropFilter: "blur(8px)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 12,
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Info size={16} color="#60a5fa" />
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              Live Session Details
            </span>
          </span>
          <ChevronDown size={16} color="#64748b" />
        </button>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            fontSize: 12,
          }}
        >
          <DetailRow label="Session" value={details.sessionName} mono />
          <DetailRow
            label="Topic covered"
            value={details.topicCovered}
            highlight
          />
          <DetailRow label="Course" value={details.courseLabel || "—"} />
          <DetailRow
            label={
              (details.batchLabel || "").split(",").filter((p) => p.trim())
                .length > 1
                ? "Batches"
                : "Batch"
            }
            value={details.batchLabel || "—"}
          />
          <DetailRow
            label="Category"
            value={details.category || "—"}
            capitalize
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              paddingTop: 4,
            }}
          >
            {details.trainerAvatarUrl ? (
              <img
                src={details.trainerAvatarUrl}
                alt={trainerName}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  objectFit: "cover",
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.1)",
                }}
              />
            ) : (
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#334155",
                  color: "#e2e8f0",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.1)",
                }}
              >
                {initial}
              </div>
            )}
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                }}
              >
                {trainerName}
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  color: "#64748b",
                }}
              >
                Trainer
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              borderTop: "1px solid rgba(255,255,255,0.1)",
              paddingTop: 10,
              color: "#94a3b8",
              fontSize: 12,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Clock size={12} />
              {details.sessionTimer || "00:00:00"}
            </span>
            {details.batchLabel ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "flex-start",
                  gap: 4,
                  maxWidth: "100%",
                }}
              >
                <Users size={12} style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ wordBreak: "break-word" }}>
                  {details.batchLabel}
                </span>
              </span>
            ) : null}
            <span style={{ textTransform: "capitalize" }}>{streamingLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
